import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import {
  actualizarDolar,
  obtenerDolarActual,
  obtenerHistorialDolar,
} from "../services/dolarService";
import { renovarTokenHippo } from "../../facturacion/services/facturacionService";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/La_Paz" });

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  if (!withTime && /^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleString("sv-SE", {
    timeZone: "America/La_Paz",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false } : {}),
  });
};

const getError = (error) =>
  error?.response?.data?.message || error?.message || "No se pudo completar la operacion.";

export default function DolarPage() {
  const [actual, setActual] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cambio: "", fecha_vigencia: today(), observacion: "" });
  const [renovarToken, setRenovarToken] = useState(true);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [actualResponse, historialResponse] = await Promise.all([
        obtenerDolarActual(),
        obtenerHistorialDolar({ page: page + 1, limit }),
      ]);
      setActual(actualResponse?.data || null);
      const historial = historialResponse?.data || {};
      setItems(historial.items || []);
      setTotal(Number(historial.total || 0));
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    } finally {
      setLoading(false);
    }
  }, [limit, page]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => {
    setForm({
      cambio: Number(actual?.cambio || 0).toFixed(2),
      fecha_vigencia: today(),
      observacion: "",
    });
    setRenovarToken(true);
    setOpen(true);
  };

  const handleCambio = (event) => {
    const value = event.target.value.replace(",", ".");
    if (value === "" || /^\d{0,8}(\.\d{0,2})?$/.test(value)) {
      setForm((current) => ({ ...current, cambio: value }));
    }
  };

  const handleSave = async () => {
    if (!Number(form.cambio) || Number(form.cambio) <= 0) {
      setToast({ open: true, severity: "warning", message: "Ingresa un tipo de cambio mayor a cero." });
      return;
    }
    if (!form.fecha_vigencia) {
      setToast({ open: true, severity: "warning", message: "Selecciona la fecha de vigencia." });
      return;
    }

    setSaving(true);
    try {
      await actualizarDolar(actual.cod, {
        cambio: Number(form.cambio).toFixed(2),
        fecha_vigencia: form.fecha_vigencia,
        observacion: form.observacion.trim(),
      });
      setOpen(false);
      setPage(0);
      await load();

      if (renovarToken) {
        try {
          await renovarTokenHippo();
          setToast({ open: true, severity: "success", message: "Tipo de cambio actualizado y token Hippo renovado." });
        } catch (tokenError) {
          setToast({
            open: true,
            severity: "warning",
            message: `Tipo de cambio actualizado, pero no se pudo renovar el token Hippo: ${getError(tokenError)}`,
          });
        }
      } else {
        setToast({ open: true, severity: "success", message: "Tipo de cambio actualizado." });
      }
    } catch (error) {
      setToast({ open: true, severity: "error", message: getError(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Tipo de cambio"
        subtitle="Actualizacion manual e historial del dolar"
        backTo="/dashboard"
        actions={(
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={openEdit}
            disabled={!actual || loading}
            sx={{ textTransform: "none" }}
          >
            Actualizar
          </Button>
        )}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 }, display: "grid", gap: 1.25 }}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                borderRadius: 1,
                bgcolor: "success.light",
                color: "success.dark",
              }}
            >
              <CurrencyExchangeIcon />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Tipo de cambio vigente</Typography>
              <Typography sx={{ fontSize: "1.65rem", lineHeight: 1.1, fontWeight: 900 }}>
                Bs {Number(actual?.cambio || 0).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ ml: { md: "auto" }, minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary">Ultima actualizacion</Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatDate(actual?.fecha_actualizacion, true)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {actual?.usuario_actualizacion || "Sin usuario registrado"}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
          <Box sx={{ px: 1.25, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle2" fontWeight={900}>Historial de cambios</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha vigencia</TableCell>
                  <TableCell align="right">Anterior</TableCell>
                  <TableCell align="right">Nuevo</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Observacion</TableCell>
                  <TableCell>Registrado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && !items.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No hay cambios registrados.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((item) => (
                  <TableRow key={item.id_historial} hover>
                    <TableCell>{formatDate(item.fecha_vigencia)}</TableCell>
                    <TableCell align="right">{Number(item.cambio_anterior).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: "success.dark" }}>
                      {Number(item.cambio_nuevo).toFixed(2)}
                    </TableCell>
                    <TableCell>{item.usuario || "-"}</TableCell>
                    <TableCell>{item.observacion || "-"}</TableCell>
                    <TableCell>{formatDate(item.fecha_registro, true)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={limit}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Limite"
          />
        </Paper>
      </Box>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Actualizar tipo de cambio</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.25, pt: 0.5 }}>
            <TextField
              size="small"
              label="Tipo de cambio *"
              value={form.cambio}
              onChange={handleCambio}
              inputProps={{ inputMode: "decimal" }}
              helperText="Valor expresado en bolivianos, con dos decimales"
            />
            <TextField
              size="small"
              type="date"
              label="Fecha de vigencia *"
              value={form.fecha_vigencia}
              onChange={(event) => setForm((current) => ({ ...current, fecha_vigencia: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              multiline
              minRows={3}
              label="Observacion"
              value={form.observacion}
              onChange={(event) => setForm((current) => ({ ...current, observacion: event.target.value }))}
              inputProps={{ maxLength: 255 }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={renovarToken}
                  onChange={(event) => setRenovarToken(event.target.checked)}
                />
              )}
              label="Actualizar token diario (Hippo)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <LoadingButton variant="contained" loading={saving} onClick={handleSave}>
            Guardar cambio
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((current) => ({ ...current, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
